import Phaser from '../lib/phaser.js';
import { DIRECTION } from '../common/direction.js';

export class Controls {
  /** @type {Phaser.Scene} */
  #scene;
  /** @type {Phaser.Types.Input.Keyboard.CursorKeys | undefined} */
  #cursorKeys;
  /** @type {boolean} */
  #lockPlayerInput;
  /** @type {Phaser.Input.Keyboard.Key | undefined} */
  #enterKey;
  /** @type {Phaser.Input.Keyboard.Key | undefined} */
  #fKey;
  /** @type {Phaser.Input.Keyboard.Key | undefined} */
  #qKey;
  /** @type {Phaser.Input.Keyboard.Key | undefined} */
  #escKey;
  /** @type {{ up: Phaser.Input.Keyboard.Key, down: Phaser.Input.Keyboard.Key, left: Phaser.Input.Keyboard.Key, right: Phaser.Input.Keyboard.Key } | undefined} WASD movement keys (alongside the arrows) */
  #wasd;
  /** @type {import('../common/direction.js').Direction} current virtual-joystick direction (touch) */
  #virtualDirection = DIRECTION.NONE;
  /** @type {import('../common/direction.js').Direction} virtual direction at the start of this frame — lets the virtual "just pressed" mirror keyboard JustDown semantics. */
  #virtualDirectionPrev = DIRECTION.NONE;
  /** @type {boolean} one-shot "A" confirm pulse from the touch action button, consumed by space/enter checks. */
  #virtualConfirm = false;

  /**
   * @param {Phaser.Scene} scene the Phaser 3 Scene the cursor keys will be created in
   */
  constructor(scene) {
    this.#scene = scene;
    this.#cursorKeys = this.#scene.input.keyboard?.createCursorKeys();
    this.#enterKey = this.#scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
    this.#fKey = this.#scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.F);
    this.#qKey = this.#scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.Q);
    this.#escKey = this.#scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    // WASD as an alternative to the arrow keys (W=up, A=left, S=down, D=right).
    const kb = this.#scene.input.keyboard;
    if (kb) {
      this.#wasd = {
        up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
        down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
        left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      };
    }
    this.#lockPlayerInput = false;
  }

  /** @type {boolean} */
  get isInputLocked() {
    return this.#lockPlayerInput;
  }

  /** @param {boolean} val the value that will be assigned */
  set lockInput(val) {
    this.#lockPlayerInput = val;
  }

  // ── Virtual (on-screen touch) input ──────────────────────────────────────────────
  // The mobile-controls HUD scene drives these so the joystick/A button feed the exact same
  // code paths as the keyboard. Virtual input is OR'd with the keyboard — it never replaces it.

  /**
   * Set the current virtual-joystick direction (DIRECTION.NONE clears it). Held until changed,
   * so it behaves like a key being held down.
   * @param {import('../common/direction.js').Direction} dir
   * @returns {void}
   */
  setVirtualDirection(dir) {
    this.#virtualDirection = dir;
  }

  /**
   * One-shot "A"/confirm pulse from the touch action button. Consumed by the next
   * wasSpaceKeyPressed() / wasEnterKeyPressed() so the A button behaves exactly like SPACE.
   * @returns {void}
   */
  pressVirtualConfirm() {
    this.#virtualConfirm = true;
  }

  /**
   * Consume the one-shot virtual confirm pulse (first caller in a frame wins).
   * @returns {boolean}
   */
  #consumeVirtualConfirm() {
    if (!this.#virtualConfirm) {
      return false;
    }
    this.#virtualConfirm = false;
    return true;
  }

  /** @returns {boolean} */
  wasEnterKeyPressed() {
    const kbd = this.#enterKey !== undefined && Phaser.Input.Keyboard.JustDown(this.#enterKey);
    return kbd || this.#consumeVirtualConfirm();
  }

  /** @returns {boolean} */
  wasSpaceKeyPressed() {
    const kbd =
      this.#cursorKeys !== undefined && Phaser.Input.Keyboard.JustDown(this.#cursorKeys.space);
    return kbd || this.#consumeVirtualConfirm();
  }

  /** Back/cancel: SHIFT (legacy) OR the ESC key — overlay scenes use this to close. */
  wasBackKeyPressed() {
    if (this.#cursorKeys === undefined) {
      return false;
    }
    if (this.#escKey !== undefined && Phaser.Input.Keyboard.JustDown(this.#escKey)) {
      return true;
    }
    return Phaser.Input.Keyboard.JustDown(this.#cursorKeys.shift);
  }

  /** @returns {boolean} */
  wasFKeyPressed() {
    if (this.#fKey === undefined) {
      return false;
    }
    return Phaser.Input.Keyboard.JustDown(this.#fKey);
  }

  /**
   * Q is the multiplayer "interact with a nearby player" key: in Badlands it challenges to a
   * duel, elsewhere it opens a trade. No-op in single-player (no nearby remote players).
   * @returns {boolean}
   */
  wasQKeyPressed() {
    if (this.#qKey === undefined) {
      return false;
    }
    return Phaser.Input.Keyboard.JustDown(this.#qKey);
  }

  /**
   * Returns if the shift key is currently being held down.
   * @returns {boolean}
   */
  isShiftKeyDown() {
    if (this.#cursorKeys === undefined) {
      return false;
    }
    return this.#cursorKeys.shift.isDown;
  }

  /** @returns {import('../common/direction.js').Direction} */
  getDirectionKeyJustPressed() {
    const w = this.#wasd;
    const justDown = (/** @type {Phaser.Input.Keyboard.Key | undefined} */ k) =>
      k !== undefined && Phaser.Input.Keyboard.JustDown(k);

    /** @type {import('../common/direction.js').Direction} */
    let selectedDirection = DIRECTION.NONE;
    if (this.#cursorKeys !== undefined) {
      if (justDown(this.#cursorKeys.left) || justDown(w?.left)) {
        selectedDirection = DIRECTION.LEFT;
      } else if (justDown(this.#cursorKeys.right) || justDown(w?.right)) {
        selectedDirection = DIRECTION.RIGHT;
      } else if (justDown(this.#cursorKeys.up) || justDown(w?.up)) {
        selectedDirection = DIRECTION.UP;
      } else if (justDown(this.#cursorKeys.down) || justDown(w?.down)) {
        selectedDirection = DIRECTION.DOWN;
      }
    }

    // Virtual joystick edge: report the direction once, the frame it first appears/changes
    // (mirrors keyboard JustDown). prev advances here so menu navigation steps once per flick.
    if (selectedDirection === DIRECTION.NONE && this.#virtualDirection !== this.#virtualDirectionPrev) {
      selectedDirection = this.#virtualDirection;
    }
    this.#virtualDirectionPrev = this.#virtualDirection;

    return selectedDirection;
  }

  /** @returns {import('../common/direction.js').Direction} */
  getDirectionKeyPressedDown() {
    /** @type {import('../common/direction.js').Direction} */
    let selectedDirection = DIRECTION.NONE;
    const w = this.#wasd;
    if (this.#cursorKeys !== undefined) {
      if (this.#cursorKeys.left.isDown || w?.left.isDown) {
        selectedDirection = DIRECTION.LEFT;
      } else if (this.#cursorKeys.right.isDown || w?.right.isDown) {
        selectedDirection = DIRECTION.RIGHT;
      } else if (this.#cursorKeys.up.isDown || w?.up.isDown) {
        selectedDirection = DIRECTION.UP;
      } else if (this.#cursorKeys.down.isDown || w?.down.isDown) {
        selectedDirection = DIRECTION.DOWN;
      }
    }

    // Held virtual-joystick direction drives continuous movement (OR'd with the keyboard).
    if (selectedDirection === DIRECTION.NONE) {
      selectedDirection = this.#virtualDirection;
    }

    return selectedDirection;
  }
}
